import { useEffect, useMemo, useState } from 'react';
import { buildHoldingsContext } from '@/experts/context';
import { AVAILABLE_LOCALES, LOCALES, useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { downloadText } from '@/lib/download';
import { formatNYClock, todayNY } from '@/market/marketHours';
import { refreshAll } from '@/market/quoteService';
import { assemblePrompt } from '@/reports/prompt';
import { MAX_SEARCHES, templateById, templateName } from '@/reports/templates';
import { useAppState } from '@/state/appStore';
import { heldOnly, type Locale, type ReportTemplateId } from '@/types';
import s from './reports.module.css';

/** Builds the template with today's holdings filled in and hands it to the user's own chat product. */
export function CopyPromptDialog({ templateId, scenario, onClose, onGenerate }: { templateId: ReportTemplateId; scenario?: string; onClose: () => void; onGenerate?: () => void }) {
  const { t, locale } = useT();
  const app = useAppState();
  const template = templateById(templateId);
  const [lang, setLang] = useState<Locale>(locale);
  const [copied, setCopied] = useState(false);
  const now = useMemo(() => Date.now(), []);

  const text = useMemo(() => {
    const held = heldOnly(app.assets); // the copied prompt matches the generated one: watch items are left out
    const context = buildHoldingsContext(held, app.quotes, app.fx);
    const maxSearches = Math.min(MAX_SEARCHES, template.estimateSearches(held) + 4);
    return assemblePrompt(template, { context, assets: held, now, locale: lang, maxSearches, scenario }).full;
  }, [app.assets, app.quotes, app.fx, template, now, lang, scenario]);

  // Same as an in-app run: the copied prompt states prices, so start from fresh quotes (the text re-renders when they land).
  useEffect(() => {
    void refreshAll(true);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / permissions): the preview is selectable, and download still works.
    }
  };

  // The prompt is the same for every chat product; only the reminder to turn on web search differs, and it is one line.
  const hintKey = template.needsWeb ? ('copy.hint.web' as const) : ('copy.hint.offline' as const);

  return (
    <div className={s.backdrop} onClick={onClose}>
      <div className={s.dialog} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <header className={s.dlgHead}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span className={`serif ${s.dlgTitle}`}>{t('copy.title')}</span>
            <span className="muted" style={{ fontSize: 13 }}>·</span>
            <span className="serif" style={{ fontSize: 20, fontWeight: 500 }}>{templateName(templateId)}</span>
          </div>
          <button type="button" className="btn quiet" style={{ padding: '8px 10px' }} onClick={onClose} aria-label={t('common.close')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </header>

        <div className={s.dlgBody}>
          <div className={s.controls}>
            <div className={s.control}>
              <span className="label">{t('copy.language')}</span>
              <div className="seg">
                {AVAILABLE_LOCALES.map((id) => <button key={id} type="button" className={cx(lang === id && 'on')} onClick={() => setLang(id)}>{LOCALES.find((l) => l.id === id)?.label ?? id}</button>)}
              </div>
            </div>
          </div>

          <div className={s.hint}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: template.needsWeb ? 'var(--delay)' : 'var(--link)', flex: 'none', marginTop: 2 }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
            <p style={{ margin: 0 }}>{t(hintKey)}</p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className={s.preHead}>
              <span className="label">{t('copy.preview')}</span>
              <span className="muted num" style={{ fontSize: 12 }}>{t('copy.size', { n: text.length.toLocaleString('en-US'), time: formatNYClock(now) })}</span>
            </div>
            <pre className={s.pre}>{text}</pre>
          </div>
        </div>

        <footer className={s.dlgFoot}>
          <p className="muted">{t('copy.privacy')}</p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {onGenerate && <button type="button" className="btn link" onClick={onGenerate}>{t('copy.instead')}</button>}
            <button type="button" className="btn quiet" onClick={() => downloadText(`assetlist-${templateId}-${todayNY(now)}.md`, text)}>{t('copy.download')}</button>
            <button type="button" className="btn primary" onClick={() => void copy()}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
              {copied ? t('copy.copied') : t('copy.all')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
